import { useCallback, useEffect, useRef, useState } from 'react';
import { useDsTheme } from '../../design/ThemeProvider';
import { isChromeless } from './navItems';

/** Tailwind `lg`, which this project overrides to MUI's 1200px. */
const RAIL_BELOW = 1200;
/** Must match `--ds-dur-base`; the tail keeps pointer-events off until the paint settles. */
const ANIMATION_MS = 250;

export type ShellTier = 'expanded' | 'rail';

const readTier = (): ShellTier =>
    window.innerWidth < RAIL_BELOW ? 'rail' : 'expanded';

export type ShellLayout = {
    tier: ShellTier,
    /** True when the sidebar should render as a rail. */
    collapsed: boolean,
    /** True while the width transition is in flight. */
    animating: boolean,
    /** This route wants no app chrome at all (kiosk screens, print view). */
    chromeless: boolean,
    toggle: () => void,
}

/**
 * Owns the shell's responsive tiers and collapse state.
 *
 * The sidebar is always docked. There used to be an off-canvas overlay tier
 * below 900px, but the toggle lives inside the sidebar, so once it slid away
 * there was no way back to the navigation. Narrow windows now keep the rail.
 * `sidebarCollapsed` (persisted, on the theme context) is the user's explicit
 * choice; `null` falls back to the tier default.
 */
export const useShellLayout = (pathname: string): ShellLayout => {
    const { sidebarCollapsed, setSidebarCollapsed } = useDsTheme();
    const [tier, setTier] = useState<ShellTier>(readTier);
    const [animating, setAnimating] = useState(false);
    const animationTimer = useRef<ReturnType<typeof setTimeout>>();

    // matchMedia rather than a resize listener: `change` fires only when a
    // threshold is actually crossed, so dragging a window inside one tier costs
    // zero renders. A resize listener storing innerWidth would re-render every
    // page in the app on every mousemove of a window drag.
    useEffect(() => {
        const query = window.matchMedia(`(min-width: ${RAIL_BELOW}px)`);
        const onChange = () => setTier(readTier());
        query.addEventListener('change', onChange);
        return () => query.removeEventListener('change', onChange);
    }, []);

    useEffect(() => () => clearTimeout(animationTimer.current), []);

    const markAnimating = useCallback(() => {
        setAnimating(true);
        clearTimeout(animationTimer.current);
        // A timer, not `transitionend`, so a missed event can never leave the
        // sidebar permanently pointer-events: none.
        animationTimer.current = setTimeout(() => setAnimating(false), ANIMATION_MS);
    }, []);

    const chromeless = isChromeless(pathname);
    const collapsed = chromeless ? true : (sidebarCollapsed ?? tier === 'rail');

    const toggle = useCallback(() => {
        markAnimating();
        // Resolve `null` against the current tier before flipping, so the first
        // click always does the visible thing rather than a no-op.
        setSidebarCollapsed(!(sidebarCollapsed ?? tier === 'rail'));
    }, [markAnimating, tier, sidebarCollapsed, setSidebarCollapsed]);

    return { tier, collapsed, animating, chromeless, toggle };
};
